// autoCompute.ts — pure cache-key logic for app.tsx's auto-compute effect: "berechne
// immer alle Szenarien, außer es hat sich nichts geändert" (user feedback). Every
// scenario's forecast/backtest result is computed automatically and kept in app state,
// keyed by a stable hash of that scenario's own INPUTS — app.tsx compares this hash
// against the one that produced the currently-held result and only (re)computes on a
// mismatch, so dragging a slider back to its previous value, switching the active tab, or
// any change elsewhere in the app (settings, realTerms, a DIFFERENT scenario editing)
// never triggers a redundant network/engine call for a scenario whose own config is
// unchanged.
//
// Deliberately excludes `label`: renaming a scenario doesn't change what it computes to,
// so a rename must never be treated as a cache miss (see hashScenarioConfig below) — only
// `mode`, `forecast` and `backtest` feed the hash.
import type { ScenarioState } from './scenario.js';

/** Recursively sorts object keys before stringifying, so two objects with the same
 * fields in a different insertion order hash identically — cheap insurance against a
 * future refactor (e.g. spreading fields in a different order) silently causing spurious
 * cache misses. Arrays keep their own order (order IS meaningful there, e.g.
 * extraContributions rows). */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** FNV-1a, 32-bit — small, dependency-free, deterministic. This is a UI compute-cache
 * key, not a security/correctness boundary: a hash COLLISION could in theory cause a
 * spurious cache hit (skip a recompute that should have happened), but the two configs
 * would then have to differ while producing the exact same 32-bit digest, astronomically
 * unlikely for the handful of scenarios this app ever holds — and even then the visible
 * effect is "the previous (still valid, still honestly computed) result stays on screen
 * one edit longer than ideal", never a wrong number attributed to the wrong input. */
function fnv1a(str: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** The stable cache key for a scenario's INPUTS (mode + forecast + backtest form state,
 * NOT its label/name). Two calls with equal inputs always return the same string; any
 * change to mode/forecast/backtest — however small — changes it. */
export function hashScenarioConfig(scenario: ScenarioState): string {
  return fnv1a(stableStringify({ mode: scenario.mode, forecast: scenario.forecast, backtest: scenario.backtest }));
}
